// @flow
import { getMsalInstance, loginRequest } from '../config/azureAuthConfig';
// $FlowFixMe - external module
import type { AuthenticationResult } from '@azure/msal-browser';

import request, { apiURL } from '../utils/api';
export type AzureAuthResponse = {
  statusCode: number,
  token: string,
  permissions: Array<any>,
  user: any,
  tokenTTL: number,
};

export type UserOption = {
  id: string,
  username: string,
  firstName: ?string,
  lastName: ?string,
  organizationId: string,
  organizationName: string,
  lastSystemAccess: ?string,
};

export type UserSelectionResponse = {
  requiresUserSelection: true,
  users: Array<UserOption>,
};

/**
 * Create an Error whose message is safe to display directly to the user.
 * Sagas check `error.isUserFacing` before using `.message` so library
 * errors with internal messages never reach the UI.
 */
export function userFacingError(message: string): Error {
  const err = new Error(message);
  // $FlowFixMe - custom property
  err.isUserFacing = true;
  return err;
}

/**
 * Azure Authentication Service
 * Handles Microsoft SSO login flow and token exchange
 */
export default class AzureAuthService {
  /**
   * Initiate Azure AD login with popup
   *
   * Flow:
   * 1. Opens Microsoft login popup
   * 2. User authenticates with Microsoft
   * 3. Returns authentication result with ID token and access token
   *
   * @returns Promise<AuthenticationResult> - Contains idToken (for auth) and accessToken (for APIs)
   * @throws Error if login fails or is cancelled
   */
  async loginWithPopup(): Promise<AuthenticationResult> {
    const msalInstance = getMsalInstance();

    try {
      const response = await msalInstance.loginPopup(loginRequest);
      return response;
    } catch (error) {
      console.error('Azure AD login failed:', error);

      // Handle user cancellation separately
      if (
        error?.errorCode === 'user_cancelled' ||
        error?.message?.includes('cancelled')
      ) {
        throw userFacingError(
          'Microsoft sign-in was cancelled. Please try again.',
        );
      }

      throw userFacingError(
        'Microsoft sign-in failed. Please try again or contact your system administrator.',
      );
    }
  }

  /**
   * Exchange Azure AD ID token for Fortress JWT
   *
   * Flow:
   * 1. Sends Azure ID token to backend
   * 2. Backend validates token and finds user by email
   * 3. Returns Fortress JWT with permissions
   * 4. For Fortress SSO: May return requiresUserSelection=true with user list
   *
   * @param azureIdToken - ID token from Azure AD (contains user identity)
   * @param selectedUserId - Optional: User ID to login as (for Fortress SSO user picker)
   * @returns Promise<AzureAuthResponse | UserSelectionResponse>
   */
  async exchangeAzureTokenForFortressJWT(
    azureIdToken: string,
    selectedUserId?: string,
  ): Promise<AzureAuthResponse | UserSelectionResponse> {
    const headers: { [string]: string } = {
      Authorization: `Bearer ${azureIdToken}`,
    };

    const options: {
      method: string,
      headers: { [string]: string },
      body?: string,
    } = {
      method: 'POST',
      headers,
    };

    // Only add body and Content-Type when selectedUserId is provided
    if (selectedUserId) {
      headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify({ selectedUserId });
    }

    try {
      const response = await request(`${apiURL}/auth/azure/login`, options);
      return response;
    } catch (error) {
      console.error('Token exchange failed:', error);

      // The request utility already extracts the error string from the response body
      // If it's a string, use it directly; otherwise use fallback
      const backendMessage = typeof error === 'string' && error ? error : null;
      const fallbackMessage =
        'Unable to complete Microsoft Single Sign-On. Please refresh the page and try again. ' +
        'If the problem continues, contact your system administrator.';
      throw userFacingError(backendMessage || fallbackMessage);
    }
  }

  /**
   * Get current Azure AD account if user is logged in
   *
   * @returns Current account or null
   */
  getCurrentAccount(): ?any {
    const msalInstance = getMsalInstance();
    const accounts = msalInstance.getAllAccounts();

    if (accounts.length > 0) {
      return accounts[0];
    }

    return null;
  }
}
